/**
 * Bridge to the "8080.AI Snap & Share" chrome extension.
 *
 * Discovers the ID of the extension currently injected into the page, then
 * uses chrome.runtime.sendMessage(extensionId, message). The configured Web
 * Store ID remains a fallback for older published extension builds.
 */

export const EXTENSION_ID = process.env.REACT_APP_SHARE_EXTENSION_ID || 'REPLACE_WITH_STORE_ID';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const PING_TIMEOUT_MS = 2000;
const DISCOVERY_TIMEOUT_MS = 500;
const WEB_BRIDGE_SOURCE = '8080-ai-web';
const EXTENSION_BRIDGE_SOURCE = '8080-ai-extension';
const DISCOVER_EXTENSION = 'DISCOVER_EXTENSION';
const EXTENSION_READY = 'EXTENSION_READY';
// Must exceed the extension's SHARE_REEL budget: tab load, delayed composer
// rendering, bounded AI guidance, media attachment, and up to 5 minutes for
// the user to click Post. 9 minutes keeps the response channel open with margin.
const SHARE_TIMEOUT_MS = 9 * 60 * 1000;
const CHECK_VIEWS_TIMEOUT_MS = 2 * 60 * 1000;

interface ChromeRuntimeLike {
  sendMessage: (extensionId: string, message: unknown, callback: (response?: unknown) => void) => void;
  lastError?: { message?: string };
}

interface ChromeWindowLike {
  chrome?: {
    runtime?: Partial<ChromeRuntimeLike>;
  };
}

interface ExtensionAnnouncement {
  source?: string;
  type?: string;
  nonce?: string;
  extensionId?: string;
}

export interface ShareReelRequest {
  shareId: string;
  platform: string;
  videoUrl: string;
  caption: string;
  automationToken?: string;
  autoSubmit: boolean;
  openInBackground?: boolean;
}

export interface ShareReelResult {
  ok: boolean;
  reelUrl?: string;
  error?: string;
}

export interface CheckViewsRequest {
  shareId: string;
  platform: string;
  reelUrl: string;
}

export interface CheckViewsResult {
  ok: boolean;
  views?: number;
  linkAlive?: boolean;
  error?: string;
}

export const normalizeCheckViewsResult = (result: CheckViewsResult): CheckViewsResult => {
  if (!result.ok) return result;
  if (result.linkAlive === false) return { ok: true, views: 0, linkAlive: false };
  const validLiveCount = (
    result.linkAlive === true
    && typeof result.views === 'number'
    && Number.isSafeInteger(result.views)
    && result.views >= 0
  );

  if (validLiveCount) return result;

  return { ok: false, error: 'The extension returned invalid view data. Please try again.' };
};

/** Convert an API-relative path (e.g. ShareItem.video_url) into an absolute URL for the extension. */
export const toAbsoluteUrl = (path: string): string => (/^https?:\/\//.test(path) ? path : `${API_BASE_URL}${path}`);

const getChromeRuntime = (): ChromeRuntimeLike | null => {
  if (typeof window === 'undefined') return null;

  const runtime = (window as unknown as ChromeWindowLike).chrome?.runtime;

  return runtime && typeof runtime.sendMessage === 'function' ? (runtime as ChromeRuntimeLike) : null;
};

let discoveredExtensionId: string | null = null;

const isExtensionId = (value: unknown): value is string => typeof value === 'string' && /^[a-p]{32}$/.test(value);

const discoverExtensionId = (): Promise<string | null> => {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (discoveredExtensionId) return Promise.resolve(discoveredExtensionId);

  return new Promise((resolve) => {
    const nonce = `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    function finish(extensionId: string | null): void {
      window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      resolve(extensionId);
    }

    function onMessage(event: MessageEvent<ExtensionAnnouncement>): void {
      const data = event.data;

      if (
        event.source !== window ||
        event.origin !== window.location.origin ||
        data?.source !== EXTENSION_BRIDGE_SOURCE ||
        data.type !== EXTENSION_READY ||
        data.nonce !== nonce ||
        !isExtensionId(data.extensionId)
      ) {
        return;
      }

      discoveredExtensionId = data.extensionId;
      finish(discoveredExtensionId);
    }

    window.addEventListener('message', onMessage);

    const timer = window.setTimeout(() => finish(null), DISCOVERY_TIMEOUT_MS);

    window.postMessage({ source: WEB_BRIDGE_SOURCE, type: DISCOVER_EXTENSION, nonce }, window.location.origin);
  });
};

const resolveExtensionId = async (): Promise<string | null> => {
  const discovered = await discoverExtensionId();

  return discovered || (isExtensionId(EXTENSION_ID) ? EXTENSION_ID : null);
};

const errorText = (err: unknown): string => (err instanceof Error ? err.message : 'Extension call failed');

const dispatchMessage = (
  runtime: ChromeRuntimeLike,
  extensionId: string,
  message: Record<string, unknown>,
  timeoutMs: number,
  resolve: (value: unknown) => void,
  reject: (reason: Error) => void
): void => {
  const timer = window.setTimeout(() => reject(new Error('Extension request timed out')), timeoutMs);

  runtime.sendMessage(extensionId, message, (response) => {
    window.clearTimeout(timer);
    const lastError = runtime.lastError;

    if (lastError || response === undefined) {
      reject(new Error(lastError?.message || 'Extension did not respond'));

      return;
    }

    resolve(response);
  });
};

async function sendExtensionMessage<T>(message: Record<string, unknown>, timeoutMs: number): Promise<T> {
  const extensionId = await resolveExtensionId();
  const runtime = getChromeRuntime();

  if (!runtime || !extensionId) {
    throw new Error('Extension not available');
  }

  return new Promise<T>((resolve, reject) => {

    try {
      dispatchMessage(runtime, extensionId, message, timeoutMs, resolve as (value: unknown) => void, reject);
    } catch (err) {
      reject(err instanceof Error ? err : new Error(errorText(err)));
    }
  });
}

/** True when the extension is installed and answering PING messages. */
export const pingExtension = async (): Promise<boolean> => {
  try {
    const response = await sendExtensionMessage<{ ok?: boolean }>({ action: 'PING' }, PING_TIMEOUT_MS);

    return Boolean(response && response.ok);
  } catch {
    return false;
  }
};

/** Ask the extension to open the platform composer and attach the reel. */
export const shareReel = async (request: ShareReelRequest): Promise<ShareReelResult> => {
  try {
    return await sendExtensionMessage<ShareReelResult>(
      { action: 'SHARE_REEL', apiBaseUrl: API_BASE_URL, ...request },
      SHARE_TIMEOUT_MS,
    );
  } catch (err) {
    return { ok: false, error: errorText(err) };
  }
};

/** Ask the extension to open the posted reel and read its view count. */
export const checkViews = async (request: CheckViewsRequest): Promise<CheckViewsResult> => {
  try {
    const result = await sendExtensionMessage<CheckViewsResult>(
      { action: 'CHECK_VIEWS', openInBackground: true, ...request },
      CHECK_VIEWS_TIMEOUT_MS,
    );

    return normalizeCheckViewsResult(result);
  } catch (err) {
    return { ok: false, error: errorText(err) };
  }
};
